import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet } from '../../utils/api';
import { usePageTitle } from '../../contexts/PageTitleContext';
import '../../css/Board/board.css';

const TAB_CONFIG = [
  { key: 'notice', label: '공지사항' },
  { key: 'event', label: '이벤트' }
];

const formatDate = (raw) => {
  if (!raw || raw === '0000-00-00 00:00:00') return '';
  const normalized = String(raw).replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return String(raw).split(' ')[0] || String(raw);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

export default function BoardList({
  fixedType,
  showTabs = true,
  title = '공지사항/이벤트',
  subtitle = '와우크루즈의 새로운 소식과 이벤트를 확인해 주세요.',
  basePath = '/board'
}) {
  const { setPageTitle } = usePageTitle();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [pageMeta, setPageMeta] = useState({ total: 0, page: 1, limit: 20 });

  const currentPage = useMemo(() => {
    const raw = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(raw) && raw > 0 ? raw : 1;
  }, [searchParams]);

  const activeType = useMemo(() => {
    if (fixedType === 'event' || fixedType === 'notice') return fixedType;
    const type = searchParams.get('type');
    return type === 'event' ? 'event' : 'notice';
  }, [fixedType, searchParams]);

  useEffect(() => {
    if (fixedType === 'notice') {
      setPageTitle('공지사항');
      return;
    }
    if (fixedType === 'event') {
      setPageTitle('이벤트');
      return;
    }
    setPageTitle('공지사항/이벤트');
  }, [fixedType, setPageTitle]);

  useEffect(() => {
    let isActive = true;
    const fetchPosts = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await apiGet('/board/get_posts.php', { type: activeType, page: currentPage });
        if (!isActive) return;
        if (data?.success) {
          setPosts(Array.isArray(data.data) ? data.data : []);
          setPageMeta({
            total: Number.isFinite(Number(data.total)) ? Number(data.total) : 0,
            page: Number.isFinite(Number(data.page)) ? Number(data.page) : currentPage,
            limit: Number.isFinite(Number(data.limit)) ? Number(data.limit) : 20
          });
        } else {
          setPosts([]);
          setError(data?.message || '게시글을 불러오지 못했습니다.');
        }
      } catch (err) {
        if (!isActive) return;
        setError('게시글을 불러오지 못했습니다.');
        setPosts([]);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchPosts();

    return () => {
      isActive = false;
    };
  }, [activeType, currentPage]);

  const handleTabChange = (nextType) => {
    if (!showTabs) return;
    if (nextType === activeType) return;
    setSearchParams({ type: nextType });
  };

  const handleItemClick = (postId) => {
    if (!postId) return;
    const detailBase = basePath || '/board';
    const pageQuery = currentPage > 1 ? `?page=${currentPage}` : '';
    navigate(`${detailBase}/${postId}${pageQuery}`);
  };

  const totalPages = Math.max(1, Math.ceil((pageMeta.total || 0) / (pageMeta.limit || 20)));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageRange = 5;
  const halfRange = Math.floor(pageRange / 2);
  let startPage = Math.max(1, safeCurrentPage - halfRange);
  let endPage = Math.min(totalPages, startPage + pageRange - 1);
  if (endPage - startPage < pageRange - 1) {
    startPage = Math.max(1, endPage - pageRange + 1);
  }
  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i += 1) {
    pageNumbers.push(i);
  }

  const handlePageChange = (nextPage) => {
    if (nextPage === currentPage) return;
    if (nextPage < 1 || nextPage > totalPages) return;
    const params = {};
    if (showTabs) params.type = activeType;
    if (nextPage > 1) params.page = String(nextPage);
    setSearchParams(params);
  };

  return (
    <div className="board-page">
      <div className="board-container">
        <div className="board-header">
          <h2 className="board-title">{title}</h2>
          <p className="board-subtitle">{subtitle}</p>
          {showTabs && (
            <div className="board-tabs" role="tablist">
              {TAB_CONFIG.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`board-tab ${activeType === tab.key ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.key)}
                  role="tab"
                  aria-selected={activeType === tab.key}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {isLoading && <div className="board-status">로딩 중...</div>}
        {!isLoading && error && <div className="board-status error">{error}</div>}
        {!isLoading && !error && posts.length === 0 && (
          <div className="board-status">등록된 글이 없습니다.</div>
        )}

        {!isLoading && !error && posts.length > 0 && (
          <div className="board-list">
            {posts.map((post) => {
              const id = post.id;
              const title = post.title || '';
              const pinned = String(post.is_pinned) === '1';
              const date = formatDate(post.published_at || post.created_at);
              return (
                <div
                  key={id}
                  className="board-item"
                  onClick={() => handleItemClick(id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') handleItemClick(id);
                  }}
                >
                  <div className="board-item-title">
                    {pinned && <span className="board-badge pinned">고정</span>} {title}
                  </div>
                  <div className="board-item-meta">
                    <span>{date}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!isLoading && !error && pageMeta.total > pageMeta.limit && (
          <div className="board-pagination">
            <button
              type="button"
              className="board-page-btn"
              disabled={safeCurrentPage <= 1}
              onClick={() => handlePageChange(safeCurrentPage - 1)}
            >
              이전
            </button>
            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                className={`board-page-btn ${page === safeCurrentPage ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              className="board-page-btn"
              disabled={safeCurrentPage >= totalPages}
              onClick={() => handlePageChange(safeCurrentPage + 1)}
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
